import { PLATFORM } from 'src/dataSource/types'
import { type Platforms } from 'shared/api/accounts'
import { type ReportTemplateId } from 'shared/api/standardReports'
import { type ConfigStatus } from 'shared/configStatuses'
import {
  calculateAverageOverValue,
  calculateCostPerThousandImpressions,
  calculateRemainingBudget,
  calculateBudgetDepletion,
  calculateClickThroughRate,
  calculateConversionRate,
  calculateCostOfSales,
  calculateCostPerAction,
  calculateCostPerClick,
  calculateRoas
} from 'shared/utils'
import { type Metric } from './reportTemplateParameters'
import { type ConversionEventsDefault } from './components/utils'
import { ALVIE_DATASOURCE_KEY, GA4_DATASOURCE_KEY } from 'shared/constants/analytics'

export const CHANNEL = 'channel'
export const MARKET = 'market'
export const DATE = 'date'
export const SPEND = 'spend'
export const CONVERSION_VALUE = 'conversionValue'
export const CONVERSIONS = 'conversions'
export const ROAS = 'roas'
export const COST_PER_ACTION = 'costPerAction'
export const DAILY_TARGET = 'dailyTarget'
export const DAILY_BUDGET = 'dailyBudget'
export const DAILY_REMAINING_BUDGET = 'dailyRemainingBudget'
export const DAILY_BUDGET_DEPLETION = 'dailyBudgetDepletion'
export const DAILY_REMAINING_TARGET = 'dailyRemainingTarget'
export const DAILY_TARGET_DEPLETION = 'dailyTargetDepletion'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const IMPRESSIONS = 'impressions'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CLICKS = 'clicks'
const CLICK_THROUGH_RATE = 'clickThroughRate'
const CONVERSION_RATE = 'conversionRate'
export const COST_PER_CLICK = 'costPerClick'
export const COST_OF_SALES = 'costOfSales'
export const COST_PER_THOUSAND_IMPRESSIONS = 'costPerThousandImpressions'
const AVERAGE_ORDER_VALUE = 'averageOrderValue'

export const DIMENSIONS = [CHANNEL, MARKET, DATE] as const

export const STATUSES_TO_SHOW_HOMEPAGE_DATA_FOR: ConfigStatus[] = ['active', 'running', 'pending', 'paused', 'error']

export const STATUSES_TO_SHOW_BANNER_FOR: ConfigStatus[] = ['paused', 'error']

export const REPORT_TEMPLATE_WITH_CONVERSION_EVENTS: ReportTemplateId[] = ['reporting_base']

// The order of this array determines the priority of default conversion events.
// The first item is considered the "primary" default, the second is "secondary", and so on.
export const CONVERSION_EVENTS_DEFAULTS: ConversionEventsDefault[] = [
  {
    source: ALVIE_DATASOURCE_KEY,
    name: 'Alvie Attribution'
  },
  {
    source: GA4_DATASOURCE_KEY,
    name: 'purchase'
  }
]

export const METRICS_WITH_REVERSED_COLOR_SCHEME = [
  COST_PER_ACTION,
  COST_PER_THOUSAND_IMPRESSIONS,
  COST_PER_CLICK,
  COST_OF_SALES
]

export const CONVERSION_SOURCE_TO_DATASOURCE_MAP: Record<string, Platforms> = {
  msads: PLATFORM.MICROSOFT_ADVERTISING,
  gads: PLATFORM.GOOGLE,
  facebook: PLATFORM.FACEBOOK,
  snapchat: PLATFORM.SNAPCHAT,
  tiktok: PLATFORM.TIKTOK,
  ga: PLATFORM.GA4,
  dv360: PLATFORM.DV360,
  linkedin: PLATFORM.LINKEDIN
}

interface CalculatedMetricMetadata {
  calculationFunction: (...args: number[]) => number | null
  dependentMetrics: Metric[]
}

export const CALCULATED_METRICS_MAP: Partial<Record<Metric, CalculatedMetricMetadata>> = {
  [COST_PER_ACTION]: {
    calculationFunction: (spend: number, conversions: number) => calculateCostPerAction(spend, conversions),
    dependentMetrics: ['spend', 'conversions']
  },
  [ROAS]: {
    calculationFunction: (conversionValue: number, spend: number) => calculateRoas(conversionValue, spend),
    dependentMetrics: ['conversionValue', 'spend']
  },
  [CLICK_THROUGH_RATE]: {
    calculationFunction: (clicks: number, impressions: number) => calculateClickThroughRate(clicks, impressions),
    dependentMetrics: ['clicks', 'impressions']
  },
  [CONVERSION_RATE]: {
    calculationFunction: (conversions: number, clicks: number) => calculateConversionRate(conversions, clicks),
    dependentMetrics: ['conversions', 'clicks']
  },
  [COST_PER_CLICK]: {
    calculationFunction: (spend: number, clicks: number) => calculateCostPerClick(spend, clicks),
    dependentMetrics: ['spend', 'clicks']
  },
  [COST_OF_SALES]: {
    calculationFunction: (spend: number, conversionValue: number) => calculateCostOfSales(spend, conversionValue),
    dependentMetrics: ['spend', 'conversionValue']
  },
  [COST_PER_THOUSAND_IMPRESSIONS]: {
    calculationFunction: (spend: number, impressions: number) =>
      calculateCostPerThousandImpressions(spend, impressions),
    dependentMetrics: ['spend', 'impressions']
  },
  [AVERAGE_ORDER_VALUE]: {
    calculationFunction: (conversionValue: number, conversions: number) =>
      calculateAverageOverValue(conversionValue, conversions),
    dependentMetrics: ['conversionValue', 'conversions']
  },
  [DAILY_REMAINING_BUDGET]: {
    calculationFunction: (spend: number, dailyBudget: number) => calculateRemainingBudget(spend, dailyBudget),
    dependentMetrics: ['spend', 'dailyBudget']
  },
  [DAILY_BUDGET_DEPLETION]: {
    calculationFunction: (spend: number, dailyBudget: number) => calculateBudgetDepletion(spend, dailyBudget),
    dependentMetrics: ['spend', 'dailyBudget']
  },
  [DAILY_REMAINING_TARGET]: {
    calculationFunction: (conversionValue: number, dailyTarget: number) =>
      calculateRemainingBudget(conversionValue, dailyTarget),
    dependentMetrics: ['conversionValue', 'dailyTarget']
  },
  [DAILY_TARGET_DEPLETION]: {
    calculationFunction: (conversionValue: number, dailyTarget: number) =>
      calculateBudgetDepletion(conversionValue, dailyTarget),
    dependentMetrics: ['conversionValue', 'dailyTarget']
  }
}
